export interface ProviderAccountIdentity {
  readonly providerAccountId: string;
  readonly legacyProviderAccountIds?: ReadonlyArray<string>;
}

/**
 * Resolves an account ID within inventory that has already been scoped to one provider.
 * Callers choose a default only when no account ID was requested.
 */
export function findProviderAccountByIdentity<T extends ProviderAccountIdentity>(
  accounts: ReadonlyArray<T>,
  selectedProviderAccountId: string | null | undefined,
): T | undefined {
  if (!selectedProviderAccountId) return undefined;
  const canonicalAccount = accounts.find(
    (account) => account.providerAccountId === selectedProviderAccountId,
  );
  if (canonicalAccount) return canonicalAccount;
  return accounts.find((account) =>
    account.legacyProviderAccountIds?.includes(selectedProviderAccountId),
  );
}

/** Resolves known aliases without replacing an explicit unavailable account. */
export function resolveProviderAccountIdentity<T extends ProviderAccountIdentity>(
  accounts: ReadonlyArray<T>,
  selectedProviderAccountId: string,
): string;
export function resolveProviderAccountIdentity<T extends ProviderAccountIdentity>(
  accounts: ReadonlyArray<T>,
  selectedProviderAccountId: string | undefined,
): string | undefined;
export function resolveProviderAccountIdentity<T extends ProviderAccountIdentity>(
  accounts: ReadonlyArray<T>,
  selectedProviderAccountId: string | undefined,
): string | undefined {
  return (
    findProviderAccountByIdentity(accounts, selectedProviderAccountId)?.providerAccountId ??
    selectedProviderAccountId
  );
}
